import { forwardRef, ForwardRefRenderFunction, useEffect, useState } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { SalaryFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FieldConstants, IconDefinitionConstants } from "utils/constants";
import { CurrencyInputProps, SearchInputProps } from "models/propTypes/wintFormPropsTypes";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModelSalary } from "wint-api-client/dist/types";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { AddExtraSalaryFormProps } from "models/propTypes/settingsPropTypes";
//#endregion
//#region components
import {
  CurrencyInput,
  GroupSection,
  SearchInput,
  useWintForm,
  VerticalFormField,
  WintForm,
} from "components/common/wintForms";
import { useSalaryService } from "serviceHooks";
import { DetailsTableViewSmall, Skeletons, StaticInfoMessage } from "components/common";
import { useDebouncedCallback } from "use-debounce";
import { KeyValue } from "models/utilTypes/keyValue";
import { CurrencyEnums } from "models/enums";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const AddExtraSalaryForm: ForwardRefRenderFunction<FormHandles, AddExtraSalaryFormProps> = (props, ref) => {
  const { wintFormProps, connect, values } = useWintForm<SalaryFormTypes.AddExtraSalary>({
    formRef: ref,
    defaultValues: props.defaultValues,
    validationSchema: props.validationSchema,
    handleSubmit: props.handleSubmit,
  });

  const { employeeSuggestions, searchEmployees } = props;

  const [salaryDetailList, setSalaryDetailList] = useState<
    Array<KeyValue<string, string> | typeof ACTION_SEPARATOR | undefined>
  >([]);
  const [loading, setLoading] = useState<boolean>(true);

  const { getExtraSalaryInfo } = useSalaryService();

  const emplpoyeeProps = connect("employee");
  const amountProps = connect("amount");

  useEffect(() => {
    setLoading(true);
    getExtraSalaryInfo(props.defaultValues)
      .then((res) => {
        const details = convertExtraSalaryDetails(res.data);
        setSalaryDetailList(details);
      })
      .catch(() => {
        setSalaryDetailList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const loadSalaryDetails = useDebouncedCallback(async () => {
    setLoading(true);
    try {
      const updateValues = {
        ...values,
        amount: UtilsHelper.isEmpty(values.amount) ? 0 : values.amount,
      };
      const response = await getExtraSalaryInfo(updateValues);
      const details = convertExtraSalaryDetails(response.data);
      setSalaryDetailList(details);
    } catch (e) {
      setSalaryDetailList([]);
    } finally {
      setLoading(false);
    }
  }, FieldConstants.DebounceTime);

  const onAmountValueChange = (e: React.ChangeEvent<any>) => {
    const amount = e.target.value as number;
    amountProps.setValue && amountProps.setValue(amount);
    loadSalaryDetails();
  };

  const onEmployeeSelect = (employee: AutocompleteListItem | null) => {
    emplpoyeeProps.setValue && emplpoyeeProps.setValue(employee);
    employee && loadSalaryDetails();
  };

  const convertExtraSalaryDetails = (details: WintSuperkollAPIModelSalary.PredictedExtraSalaryDetails) => {
    const data: Array<KeyValue<string, string> | typeof ACTION_SEPARATOR | undefined> = [
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.TotalGrossSalary),
        LocalizationHelperInstance.getFormattedCurrency(details.totalGrossSalary, CurrencyEnums.CurrencyCode.SEK)
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.TotalTax),
        LocalizationHelperInstance.getFormattedCurrency(details.totalSalaryTax, CurrencyEnums.CurrencyCode.SEK)
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.EmployerContribution),
        LocalizationHelperInstance.getFormattedCurrency(
          details.totalEmployerContribution,
          CurrencyEnums.CurrencyCode.SEK
        )
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.TaxOnExtraSalary),
        LocalizationHelperInstance.getFormattedCurrency(details.taxForExtraSalary, CurrencyEnums.CurrencyCode.SEK)
      ),
      ACTION_SEPARATOR,
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.NetSalaryForExtraSalary),
        LocalizationHelperInstance.getFormattedCurrency(details.netPayoutForExtraSalary, CurrencyEnums.CurrencyCode.SEK)
      ),
    ];
    return data;
  };

  return (
    <>
      <WintForm {...wintFormProps}>
        <GroupSection
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.AddExtraSalaryDescription)}
          className={cssExports.noBorder}
        >
          <VerticalFormField<SearchInputProps>
            Component={SearchInput}
            {...emplpoyeeProps}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.AddExtraSalaryEmployee)}
            icon={IconDefinitionConstants.WUser}
            items={searchEmployees}
            suggestedItems={employeeSuggestions}
            setValue={onEmployeeSelect}
          />
          <VerticalFormField<CurrencyInputProps>
            Component={CurrencyInput}
            {...amountProps}
            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.AddExtraSalaryAmountToBePaid)}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Salary.AddExtraSalaryAmountToBePaidDescription
            )}
            currency={CurrencyEnums.CurrencyCode.SEK}
            icon={IconDefinitionConstants.WMoney}
            onChange={onAmountValueChange}
          />
        </GroupSection>
      </WintForm>
      {values.employee ? (
        <>
          <StaticInfoMessage gutterBottom>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.SalaryDetailInfoMessage)}
          </StaticInfoMessage>
          {loading ? (
            <Skeletons.DetailTableViewSmallSkeleton />
          ) : (
            <DetailsTableViewSmall hideButton={true} detailsList={salaryDetailList} />
          )}
        </>
      ) : (
        <> </>
      )}
    </>
  );
};

export default forwardRef(AddExtraSalaryForm);
